import React, { MouseEventHandler, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Destination, Organization } from "../common/ObjectTypes";
import SelectInput from "../common/SelectInput";
import { AttentionIcon } from "../common/ProjectIcons";
import { isActionVisible } from "../common/SchemaFormTemplates/checkSchema";
import { baseUrlIsRequired } from "../routes/providerBaseUrl";
import { isEarthRangerDestinationType } from "./earthRangerUrl";
import EarthRangerSiteInput from "./EarthRangerSiteInput";
import { buttonClasses } from "../common/tailwindClassnames";
import { useHelp, HelpCard } from "../../context/HelpContext";
import SelectionPanel from "../common/configPanel/SelectionPanel";
import ActionSectionForm from "../common/configPanel/ActionSectionForm";
import { useConfigEditor } from "../common/configPanel/useConfigEditor";
import UnsavedSectionPrompt, { PendingEdits, SectionPrompt } from "../common/configPanel/UnsavedSectionPrompt";
import { DETAILS_SLUG, actionForSlug, buildActionNav } from "../common/configPanel/buildActionNav";
import HowToConnect from "../common/configPanel/HowToConnect";
import { getRouteApiKey } from "../../api/routes/routesApi";
import { ConfigTarget } from "../common/configPanel/actionSetup";

type DestinationDetailsModalConfigProps = {
    currentMemberIsAdmin: boolean,
    destination: Destination,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onChange: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    onChangeSelectedDestinationOwner: Function,
    // Saves the destination's own fields (name, workspace, URL) — the action
    // configurations save themselves, one section at a time.
    // Resolves false when the save didn't land, so the section-switch prompt can
    // keep the user on the section instead of moving on over an invalid form.
    onSave: (e: React.FormEvent<Element>) => Promise<boolean | void> | void,
    // Re-fetch the destination: after a section save, and to revert the details
    // section when the user cancels.
    onReloadDestination: () => void,
    // Awaitable variant, when the parent can tell us the fetch landed.
    onReloadDestinationAndWait?: () => Promise<void>,
    onToggleModal: MouseEventHandler,
    changesMade: boolean,
    errors: any,
    authToken?: string,
    // The tabs above this panel live in the parent, which has no view of what is
    // stashed in here — it consults this before navigating.
    pendingEditsRef?: React.MutableRefObject<PendingEdits | null>,
}

// Same shell as a Route's configuration panel: a section nav on the left,
// one section at a time, and a Save that commits just that section.
const DestinationDetailsModalConfig = ({
    currentMemberIsAdmin,
    destination,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onChange,
    onTestAction,
    testActionLoading,
    testActionResponse,
    onChangeSelectedDestinationOwner,
    onSave,
    onReloadDestination,
    onReloadDestinationAndWait,
    onToggleModal,
    changesMade,
    errors,
    authToken,
    pendingEditsRef,
}: DestinationDetailsModalConfigProps) => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();
    const [activeSlug, setActiveSlug] = useState<string>(DETAILS_SLUG);
    const [sectionPrompt, setSectionPrompt] = useState<SectionPrompt | null>(null);
    // Only a few technologies are reached at an address the user supplies. For
    // the rest the connector knows its own endpoint, so the field is an optional
    // override — and it moves out of General into its own Endpoint section rather
    // than sitting among the fields that are actually required.
    const urlIsRequired = baseUrlIsRequired(destination.type.value);
    const isEarthRanger = isEarthRangerDestinationType(destination.type.name);

    useEffect(() => {
        const cards: HelpCard[] = [
            {
                title: t('Setting up a Connection in Gundi'),
                description: t('Find step-by-step instructions for connecting a system to Gundi, as a source or a destination.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections-gundi/connections-in-gundi' },
            },
            ...(destination.type.help_center_url ? [{
                title: t('{{technology}} Destination Set Up', { technology: destination.type.name }),
                description: t('Configuration guide specific to {{technology}} destinations.', { technology: destination.type.name }),
                button: { label: t('Learn More'), url: destination.type.help_center_url },
            }] : []),
            {
                title: t('Add a Destination to a Route'),
                description: t('Learn the three ways to add a destination to a Route.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/routes-gundi/add-destination-to-route' },
            },
        ];
        setHelpContent({ cards });
    }, [destination.type, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    // Exactly the set the flow map's node panel offers — one predicate for both,
    // so the two navigations can never disagree. That includes the reference
    // lookups and executable helpers grouped under Advanced Config there.
    const actions = useMemo(
        () => (destination.type.actions ?? []).filter(isActionVisible),
        [destination.type.actions]);

    const target: ConfigTarget = useMemo(() => ({
        id: destination.id,
        actions: destination.type.actions || [],
        configurations: destination.configurations || [],
    }), [destination.id, destination.type.actions, destination.configurations]);

    const editor = useConfigEditor({
        token: authToken, t, successMessage: t("Destination updated"),
        // Awaitable when the parent offers it: useConfigEditor drops its stash
        // only after this resolves, and dropping it against the pre-save record
        // makes the field flash its old value.
        onSaved: onReloadDestinationAndWait ?? (() => onReloadDestination()),
    });

    const nav = buildActionNav({ target, actions, t, detailsNeedsSetup: Object.keys(errors || {}).length > 0, authBadgesOnly: true });
    // Same CONNECT section the flow map's account panels carry: the API key any
    // developer pushing data through this Connection needs.
    nav.push({ cat: t("CONNECT"), items: [{ slug: "how-to-connect", label: t("API key") }] });
    const [apiKey, setApiKey] = useState<string | undefined>(undefined);
    const onHowToConnect = activeSlug === "how-to-connect";
    // Only the CONNECT section shows the key, and most visits never open it —
    // fetching on mount spent a request (and logged an error for technologies
    // whose key endpoint 404s) on every detail open. Same lazy rule the flow
    // map's account panels follow. Requested-once ref, not the value itself:
    // a re-render before the response resolves would fire a second request.
    const apiKeyRequested = useRef(false);
    useEffect(() => {
        if (!onHowToConnect || !authToken || !destination.id || apiKeyRequested.current) return;
        apiKeyRequested.current = true;
        // No cancelled-guard: skipping the write while apiKeyRequested stayed set
        // stranded the section on "Loading…" for the modal's lifetime.
        getRouteApiKey(authToken, destination.id)
            .then((r: any) => {
                if (r?.api_key) {
                    setApiKey(r.api_key);
                    return;
                }
                // Resolved without a key: a swallowed 401. Allow a retry rather
                // than leaving the section on "Loading…" forever.
                apiKeyRequested.current = false;
            })
            .catch((e: any) => {
                apiKeyRequested.current = false;
                console.error("failed to load API key", e);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onHowToConnect, authToken, destination.id]);
    const activeAction = actionForSlug(actions, activeSlug);
    const onDetails = !onHowToConnect && (activeSlug === DETAILS_SLUG || !activeAction);

    // Saving is per section here too, so moving to another one with unsaved edits
    // asks whether to save them or discard them — the same question the flow map's
    // configuration panel asks, through the same dialog.
    const selectSection = (slug: string) => {
        if (slug === activeSlug) return;
        if (onDetails && changesMade) {
            return setSectionPrompt({
                onSave: () => onSave({ preventDefault: () => {} } as React.FormEvent<Element>),
                // The details fields are the destination record itself, so reverting
                // them means re-reading it rather than dropping a local stash.
                onDiscard: () => onReloadDestination(),
                proceed: () => setActiveSlug(slug),
            });
        }
        if (activeAction && editor.isActionDirty(target.id, activeAction.id)) {
            return setSectionPrompt({
                onSave: () => editor.save(target, activeAction.id),
                onDiscard: () => editor.cancel(target, activeAction.id),
                proceed: () => setActiveSlug(slug),
            });
        }
        setActiveSlug(slug);
    };

    // Kept current for the parent, which owns the tabs and the routes out of the
    // page. Assigned in an effect rather than during render so a render that is
    // thrown away never leaves a stale answer behind.
    useEffect(() => {
        if (!pendingEditsRef) return;
        pendingEditsRef.current = {
            hasEdits: () => (onDetails && changesMade) || editor.anyDirty,
            save: async () => {
                if (onDetails && changesMade) return (await onSave(
                    { preventDefault: () => {} } as React.FormEvent<Element>)) !== false;
                if (activeAction) return editor.save(target, activeAction.id);
                return true;
            },
            discard: () => {
                if (onDetails && changesMade) return onReloadDestination();
                if (activeAction) editor.cancel(target, activeAction.id);
            },
        };
        return () => { pendingEditsRef.current = null; };
    });

    // Only the technologies Gundi reaches at a supplied address get this field at
    // all; for everyone else the connector knows its own endpoint, and an extra
    // box competing with whatever the auth config already asks for is worse than
    // no box.
    const urlRow = (
        <div className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                <div className="md:w-1/2 flex flex-col">
                    <label className="mr-1">{isEarthRanger ? t("Site URL") : t("URL")} *</label>
                    <span className="text-sm text-secondary-gray mt-1">{isEarthRanger
                        ? t("The name of the EarthRanger site (e.g. my-site).")
                        : t("The base URL of this Connection.")}</span>
                </div>
                <div className="md:w-1/2">
                    {isEarthRanger ? (
                        <EarthRangerSiteInput
                            value={destination.base_url || ""}
                            onChange={(e) => onChange(e)}
                            error={errors.base_url}
                        />
                    ) : (
                        <>
                            <input
                                id="base_url"
                                placeholder=""
                                className="w-full p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                                value={destination.base_url || ""}
                                onChange={(e) => onChange(e)}>
                            </input>
                            {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    const detailsSection = (
        <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
            {/* Name */}
            <div className="mb-[8px] border-b border-divider-lines pt-1 pb-[8px] transition-all duration-300 w-full">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                    <div className="md:w-1/2 flex flex-col">
                        <label className="mr-1">{t("Name")} *</label>
                        <span className="text-sm text-secondary-gray mt-1">{t("A descriptive name for this Connection.")}</span>
                    </div>
                    <div className="md:w-1/2">
                        <input
                            id="name"
                            placeholder=""
                            className="w-full p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 transition-colors duration-300"
                            value={destination.name || ""}
                            onChange={(e) => onChange(e)}>
                        </input>
                        {errors.name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
                    </div>
                </div>
            </div>
            {/* Workspace */}
            <div className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                    <div className="md:w-1/2 flex flex-col">
                        <label className="mr-1">{t("Workspace")} *</label>
                        <span className="text-sm text-secondary-gray mt-1">{t("The workspace this Connection belongs to.")}</span>
                    </div>
                    <div className="md:w-1/2">
                        <SelectInput
                            options={organizations.map((org: Organization) => ({ label: org.name, value: org.id }))}
                            className={"basic-single pb-1"}
                            value={{ label: destination.owner.name, value: destination.owner.id }}
                            autoSelectSingleOption={true}
                            isDisabled={false}
                            isLoading={false}
                            isClearable={false}
                            isSearchable={true}
                            isMulti={false}
                            name={t("Workspace")}
                            onChange={(e: any) => onChangeSelectedDestinationOwner(e)}
                            onMenuScrollToBottom={loadMoreOrgs}
                            handleInputChange={orgInputChange}
                        />
                        {errors.owner && <div className="text-orange-600 bg-orange-100 py-1 px-2 rounded"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
                    </div>
                </div>
            </div>
            {urlIsRequired && urlRow}
        </div>
    );

    // Save/Cancel belong to the section on screen: the details fields PATCH the
    // destination record, an action section PATCHes just its configuration.
    const detailsFooter = (
        <>
            <button className={buttonClasses.primary} type="submit" disabled={!changesMade}
                onClick={(e: any) => onSave(e)}>
                {t("Save")}
            </button>
            <button className={buttonClasses.cancel} type="button"
                onClick={changesMade ? () => onReloadDestination() : onToggleModal}>
                {t("Cancel")}
            </button>
        </>
    );

    const actionFooter = activeAction ? (
        <>
            <button className={buttonClasses.primary}
                disabled={!editor.isActionDirty(target.id, activeAction.id) && !editor.canCommitDefaults(target, activeAction)}
                onClick={() => { editor.commitDefaults(target, activeAction); editor.save(target, activeAction.id); }}>
                {t("Save")}
            </button>
            <button className={buttonClasses.cancel} disabled={!editor.isActionDirty(target.id, activeAction.id)}
                onClick={() => editor.cancel(target, activeAction.id)}>
                {t("Cancel")}
            </button>
        </>
    ) : undefined;

    return (
        <div className="pb-16">
            <SelectionPanel
                title={destination.name || t("Destination")}
                // No address is not worth a label of its own — plenty of
                // technologies derive theirs from their configuration.
                subtitle={destination.base_url
                    ? <span className="flex items-center gap-2">· {destination.base_url}</span>
                    : undefined}
                nav={nav}
                activeSlug={onDetails ? DETAILS_SLUG : activeSlug}
                onSelectSection={selectSection}
                footer={onHowToConnect ? undefined : onDetails ? detailsFooter : actionFooter}>
                {onHowToConnect
                    ? <HowToConnect
                        kind={(destination.type as any)?.webhook ? "webhook" : "api-push"}
                        integrationType={(destination.type as any)?.value || ""}
                        apiKey={apiKey}
                        isAdmin={currentMemberIsAdmin} />
                    : onDetails
                    ? detailsSection
                    : <ActionSectionForm
                        target={target}
                        action={activeAction}
                        userIsAdmin={currentMemberIsAdmin}
                        token={authToken}
                        formVersion={editor.formVersion[target.id] || 0}
                        errors={editor.formErrors[target.id]?.[activeAction.id]}
                        formData={editor.formDataFor(target, activeAction)}
                        onChange={(data: any, fieldId?: string) => editor.snapshotChange(target, activeAction.id, data, fieldId)}
                        test={{ onTestAction: (id: any, value: any) => onTestAction(id, value), loading: testActionLoading, response: testActionResponse }}
                      />}
            </SelectionPanel>
            <UnsavedSectionPrompt prompt={sectionPrompt} onClose={() => setSectionPrompt(null)} />
        </div>
    )
}

export default DestinationDetailsModalConfig;
